"use client";

import React, { createContext, useContext, useState, useEffect, ReactNode, useRef, useCallback } from 'react';

// Audio categories
export enum AUDIO_CATEGORY {
    TIMER = 'timer',
    OTHERS = 'others'
}

// Audio files enum for easy access
export enum AUDIO {
    TICK = 'tick',
    CLICK = 'click',
    COMPLETE = 'complete',
    HOVER = 'hover',
}

// Audio configuration mapping
const AUDIO_CONFIG: Record<AUDIO, { file: string; category: AUDIO_CATEGORY }> = {
    [AUDIO.TICK]: { file: '/sounds/tick.mp3', category: AUDIO_CATEGORY.TIMER },
    [AUDIO.CLICK]: { file: '/sounds/click.mp3', category: AUDIO_CATEGORY.OTHERS },
    [AUDIO.COMPLETE]: { file: '/sounds/complete.mp3', category: AUDIO_CATEGORY.TIMER },
    [AUDIO.HOVER]: { file: '/sounds/hover.mp3', category: AUDIO_CATEGORY.OTHERS },
};

const AUDIO_SETTINGS_KEY = 'eorguessr_audio_settings';

interface AudioSettings {
    timerEnabled: boolean;
    othersEnabled: boolean;
    volume: number;
}

const DEFAULT_SETTINGS: AudioSettings = {
    timerEnabled: true,
    othersEnabled: false,
    volume: 0.5
};

interface AudioContextType {
    timerEnabled: boolean;
    othersEnabled: boolean;
    volume: number;
    setTimerEnabled: (enabled: boolean) => void;
    setOthersEnabled: (enabled: boolean) => void;
    setVolume: (volume: number) => void;
    play: (audio: AUDIO) => void;
    isLoaded: boolean;
}

const AudioContext = createContext<AudioContextType | undefined>(undefined);

interface AudioProviderProps {
    children: ReactNode;
}

export const AudioProvider: React.FC<AudioProviderProps> = ({ children }) => {
    const [timerEnabled, setTimerEnabledState] = useState<boolean>(DEFAULT_SETTINGS.timerEnabled);
    const [othersEnabled, setOthersEnabledState] = useState<boolean>(DEFAULT_SETTINGS.othersEnabled);
    const [volume, setVolumeState] = useState<number>(DEFAULT_SETTINGS.volume);
    const [isLoaded, setIsLoaded] = useState(false);
    
    // Store audio elements for reuse
    const audioElements = useRef<Map<AUDIO, HTMLAudioElement>>(new Map());

    // Load settings from localStorage on mount
    useEffect(() => {
        try {
            const savedSettings = localStorage.getItem(AUDIO_SETTINGS_KEY);
            if (savedSettings) {
                const settings: AudioSettings = JSON.parse(savedSettings);
                setTimerEnabledState(settings.timerEnabled ?? DEFAULT_SETTINGS.timerEnabled);
                setOthersEnabledState(settings.othersEnabled ?? DEFAULT_SETTINGS.othersEnabled);
                setVolumeState(settings.volume ?? DEFAULT_SETTINGS.volume);
            }
        } catch (error) {
            console.warn('Failed to load audio settings from localStorage:', error);
        }
        setIsLoaded(true);
    }, []);

    // Save settings to localStorage whenever they change
    useEffect(() => {
        if (isLoaded) {
            try {
                const settings: AudioSettings = {
                    timerEnabled,
                    othersEnabled,
                    volume
                };
                localStorage.setItem(AUDIO_SETTINGS_KEY, JSON.stringify(settings));
            } catch (error) {
                console.warn('Failed to save audio settings to localStorage:', error);
            }
        }
    }, [timerEnabled, othersEnabled, volume, isLoaded]);

    // Update timer enabled state
    const setTimerEnabled = (enabled: boolean) => {
        setTimerEnabledState(enabled);
    };

    // Update others enabled state
    const setOthersEnabled = (enabled: boolean) => {
        setOthersEnabledState(enabled);
    };

    // Update volume
    const setVolume = (newVolume: number) => {
        setVolumeState(Math.max(0, Math.min(1, newVolume)));
    };

    // Play audio function
    const play = useCallback((audio: AUDIO) => {
        const config = AUDIO_CONFIG[audio];
        if (!config) {
            console.warn(`Audio ${audio} not found in configuration`);
            return;
        }

        // Check if the category is enabled
        const isEnabled = 
            (config.category === AUDIO_CATEGORY.TIMER && timerEnabled) ||
            (config.category === AUDIO_CATEGORY.OTHERS && othersEnabled);

        if (!isEnabled) {
            return;
        }

        // Get or create audio element
        let audioElement = audioElements.current.get(audio);
        if (!audioElement) {
            audioElement = new Audio(config.file);
            audioElements.current.set(audio, audioElement);
        }

        // Set volume and play
        audioElement.volume = volume;
        audioElement.currentTime = 0; // Reset to start
        audioElement.play().catch((error) => {
            console.warn(`Failed to play audio ${audio}:`, error);
        });
    }, [timerEnabled, othersEnabled, volume]);

    const value: AudioContextType = {
        timerEnabled,
        othersEnabled,
        volume,
        setTimerEnabled,
        setOthersEnabled,
        setVolume,
        play,
        isLoaded
    };

    return (
        <AudioContext.Provider value={value}>
            {children}
        </AudioContext.Provider>
    );
};

export const useAudio = (): AudioContextType => {
    const context = useContext(AudioContext);
    if (!context) {
        // Return a no-op implementation instead of throwing
        // This prevents errors during dynamic imports or context transitions
        return {
            timerEnabled: false,
            othersEnabled: false,
            volume: 0.5,
            setTimerEnabled: () => {},
            setOthersEnabled: () => {},
            setVolume: () => {},
            play: () => {},
            isLoaded: false
        };
    }
    return context;
};
